'use client';
import React, { useEffect, useState, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import { subscribeToPush, requestNotificationPermission, isRunningAsPWA } from '@/lib/pushNotifications';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function PWARegister() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showNotifBanner, setShowNotifBanner] = useState(false);
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>('default');
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }

    // Check if already installed as PWA
    setIsInstalled(isRunningAsPWA());

    // Check notification permission
    if ('Notification' in window) {
      setNotifPermission(Notification.permission);
    }

    // Capture install prompt
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);

      // Show install banner only if not already installed and not dismissed before
      const dismissed = sessionStorage.getItem('pwa-install-dismissed');
      if (!dismissed && !isRunningAsPWA()) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Detect when app is installed
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowInstallBanner(false);
      setInstallPrompt(null);
      // After install, prompt for notifications
      setTimeout(() => {
        if ('Notification' in window && Notification.permission === 'default') {
          setShowNotifBanner(true);
        }
      }, 2000);
    });

    // Show notification banner if installed but notifications not granted
    if (isRunningAsPWA() && 'Notification' in window && Notification.permission === 'default') {
      const notifDismissed = sessionStorage.getItem('pwa-notif-dismissed');
      if (!notifDismissed) {
        setTimeout(() => setShowNotifBanner(true), 1500);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallBanner(false);
      setInstallPrompt(null);
    }
  }, [installPrompt]);

  const handleDismissInstall = useCallback(() => {
    setShowInstallBanner(false);
    sessionStorage.setItem('pwa-install-dismissed', '1');
  }, []);

  const handleEnableNotifications = useCallback(async () => {
    const permission = await requestNotificationPermission();
    setNotifPermission(permission);
    if (permission === 'granted') {
      await subscribeToPush();
    }
    setShowNotifBanner(false);
    sessionStorage.setItem('pwa-notif-dismissed', '1');
  }, []);

  const handleDismissNotif = useCallback(() => {
    setShowNotifBanner(false);
    sessionStorage.setItem('pwa-notif-dismissed', '1');
  }, []);

  return (
    <>
      {/* ── Install Banner ── */}
      {showInstallBanner && !isInstalled && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-sm z-[100] animate-slide-up">
          <div className="bg-card border border-border rounded-2xl shadow-xl p-4 flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-primary/10 flex items-center justify-center">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/assets/images/app_logo.png" alt="Betaland Partner App" className="w-10 h-10 object-contain" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground leading-tight">Pasang Aplikasi Partner</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                Install ke Home Screen untuk akses cepat & notifikasi real-time
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleInstall}
                  className="flex-1 bg-primary text-white text-xs font-semibold py-2 px-3 rounded-lg active:scale-95 transition-all"
                >
                  Install Sekarang
                </button>
                <button
                  onClick={handleDismissInstall}
                  className="text-xs text-muted-foreground py-2 px-3 rounded-lg border border-border active:scale-95 transition-all"
                >
                  Nanti
                </button>
              </div>
            </div>
            <button onClick={handleDismissInstall} className="text-muted-foreground flex-shrink-0 -mt-1 -mr-1 p-1">
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>
        </div>
      )}

      {/* ── Notification Permission Banner ── */}
      {showNotifBanner && notifPermission !== 'granted' && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-sm z-[100] animate-slide-up">
          <div className="bg-card border border-border rounded-2xl shadow-xl p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange/10 flex items-center justify-center flex-shrink-0">
              <Icon name="BellIcon" size={22} className="text-orange-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground leading-tight">Aktifkan Notifikasi</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                Terima notifikasi lead baru, booking, & persetujuan komisi secara real-time
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleEnableNotifications}
                  className="flex-1 bg-primary text-white text-xs font-semibold py-2 px-3 rounded-lg active:scale-95 transition-all"
                >
                  Aktifkan
                </button>
                <button
                  onClick={handleDismissNotif}
                  className="text-xs text-muted-foreground py-2 px-3 rounded-lg border border-border active:scale-95 transition-all"
                >
                  Lewati
                </button>
              </div>
            </div>
            <button onClick={handleDismissNotif} className="text-muted-foreground flex-shrink-0 -mt-1 -mr-1 p-1">
              <Icon name="XMarkIcon" size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
